import React from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { Container, Section } from '../../styles/GlobalStyles';
import NetworkBackground from '../Particles/NetworkBackground';

const HeroContainer = styled(Section)`
  background: ${theme.colors.neutral.white};
  padding-top: calc(80px + ${theme.spacing['3xl']});
  padding-bottom: ${theme.spacing['4xl']};
  position: relative;
  overflow: hidden;

  @media (max-width: ${theme.breakpoints.md}) {
    padding-top: calc(70px + ${theme.spacing.xl});
    padding-bottom: ${theme.spacing.xl};
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    padding-top: calc(70px + ${theme.spacing.lg});
    padding-bottom: ${theme.spacing.lg};
    overflow-x: hidden;
    overflow-y: visible;
    width: 100%;
    max-width: 100vw;
  }
`;

const HeroContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing['4xl']};
  align-items: center;

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.xl};
    text-align: center;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    display: flex;
    flex-direction: column;
    gap: 0;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }
`;

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};

  @media (max-width: ${theme.breakpoints.sm}) {
    gap: ${theme.spacing.xs};
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    padding: 0 ${theme.spacing.md};
  }
`;

const IconWrapper = styled.div<{ color: string }>`
  display: none;
`;

const Title = styled.h1<{ $highlightColor?: string }>`
  margin-bottom: ${theme.spacing.md};
  user-select: none;
  
  .highlight {
    color: ${props => props.$highlightColor || theme.colors.primary.synapse};
    position: relative;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    margin-bottom: 0;
    position: relative;
    z-index: 2;
    min-height: 220px;
    display: block;
    text-align: center;
    padding: ${theme.spacing.md};
    padding-top: ${theme.spacing['2xl']};
    line-height: 1.35;
    font-size: ${theme.typography.fontSize['3xl']};
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }
`;

const Subtitle = styled.p`
  font-size: ${theme.typography.fontSize['2xl']};
  color: ${theme.colors.neutral.gray600};
  line-height: ${theme.typography.lineHeight.relaxed};
  font-weight: ${theme.typography.fontWeight.medium};

  @media (max-width: ${theme.breakpoints.sm}) {
    font-size: ${theme.typography.fontSize.base};
    position: relative;
    z-index: 2;
    margin-bottom: 0;
  }
`;

const Description = styled.p`
  font-size: ${theme.typography.fontSize.lg};
  color: ${theme.colors.neutral.gray700};
  line-height: ${theme.typography.lineHeight.relaxed};

  @media (max-width: ${theme.breakpoints.sm}) {
    font-size: ${theme.typography.fontSize.sm};
    position: relative;
    z-index: 2;
    margin-top: 0;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    word-wrap: break-word;
    overflow-wrap: break-word;
    text-align: center;
  }
`;

const CTAGroup = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.lg};

  @media (max-width: ${theme.breakpoints.lg}) {
    justify-content: center;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column;
    width: 100%;
    position: relative;
    z-index: 2;
  }
`;

const PrimaryButton = styled.button<{ color: string }>`
  background: ${props => props.color};
  color: ${theme.colors.neutral.white};
  padding: ${theme.spacing.md} ${theme.spacing['2xl']};
  border-radius: ${theme.borderRadius.lg};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-size: ${theme.typography.fontSize.base};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  transition: all ${theme.transitions.normal};

  &:hover {
    transform: translateY(-2px);
    opacity: 0.9;
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    justify-content: center;
    width: 100%;
  }
`;

const SecondaryButton = styled.button`
  background: transparent;
  color: ${theme.colors.neutral.gray700};
  padding: ${theme.spacing.md} ${theme.spacing['2xl']};
  border-radius: ${theme.borderRadius.lg};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-size: ${theme.typography.fontSize.base};
  border: 2px solid ${theme.colors.neutral.gray300};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  transition: all ${theme.transitions.normal};

  &:hover {
    border-color: ${theme.colors.neutral.gray400};
    background: ${theme.colors.neutral.light};
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    justify-content: center;
    width: 100%;
  }
`;

const VisualContent = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  position: relative;
  z-index: 1;

  @media (max-width: ${theme.breakpoints.lg}) {
    min-height: 250px;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 220px;
    min-height: 220px;
    pointer-events: none;
  }
`;

interface ProductHeroProps {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  description: string;
  color: string;
  onPrimaryClick?: () => void;
  onSecondaryClick?: () => void;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  visualContent?: React.ReactNode;
}

const ProductHero: React.FC<ProductHeroProps> = ({
  icon,
  title,
  subtitle,
  description,
  color,
  onPrimaryClick,
  onSecondaryClick,
  primaryButtonText = "Fale Conosco",
  secondaryButtonText = "Ver Mais",
  visualContent,
}) => {
  return (
    <HeroContainer>
      <NetworkBackground accentColor={color} />
      <Container style={{ position: 'relative', zIndex: 2 }}>
        <HeroContent>
          <TextContent>
            <IconWrapper color={color}>
              {icon}
            </IconWrapper>
            
            <Title $highlightColor={color} dangerouslySetInnerHTML={{ __html: title }} />
            
            <Subtitle>{subtitle}</Subtitle>
            
            <Description>{description}</Description>

            <CTAGroup>
              <PrimaryButton color={color} onClick={onPrimaryClick}>
                {primaryButtonText}
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 3L11 8L6 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </PrimaryButton>
              
              {onSecondaryClick && (
                <SecondaryButton onClick={onSecondaryClick}>
                  {secondaryButtonText}
                </SecondaryButton>
              )}
            </CTAGroup>
          </TextContent>

          <VisualContent>
            {visualContent}
          </VisualContent>
        </HeroContent>
      </Container>
    </HeroContainer>
  );
};

export default ProductHero;


